/**
 * One shared record of the token the native shell handed us.
 *
 * useHybridAuth used to keep this in component state, so every screen read
 * localStorage on its own mount. Home could be polling happily on a token it
 * read an hour ago while a call screen opened a second later found the key
 * gone and treated the driver as logged out, with no request ever sent
 * (Lima Tow, Oct 1 2026). Keeping the token in one module-level store means
 * every screen agrees on whether there is a session, and a token the app
 * already holds in memory is never forgotten because a storage key vanished.
 *
 * The store only changes through activate() and clear(). The 401 handlers
 * that remove the storage key do not touch it: a dead token then fails its
 * request and that request's 401 is what tells the UI the session expired.
 */
import { isEmbeddedMode } from './embeddedMode';
import { isNative, onNative } from '../lib/nativeBridge';
import { devLog } from './devLog';

type Listener = () => void;

let token: string | null = null;
let bootstrapped = false;
/** A v2 wrapper has said `token` or `signedOut` since the page loaded. */
let answered = false;
let teardown: (() => void) | null = null;
const listeners = new Set<Listener>();

/**
 * How long a page in a v2 wrapper waits for `token` or `signedOut` before
 * treating itself as signed out. The wrapper sends one after every load,
 * so this only matters when it does not.
 */
export const SESSION_ANSWER_TIMEOUT_MS = 5000;

const emit = () => {
  listeners.forEach((listener) => listener());
};

export const nativeSession = {
  getToken: (): string | null => token,

  /**
   * In a v2 wrapper, true until it says whether there is a session. The
   * page shows its splash meanwhile: drawing the landing page and then
   * swapping it for Home a beat later, when the token lands, looked like
   * being signed out.
   */
  isWaiting: (): boolean => isNative() && token === null && !answered,

  activate: (next: string): void => {
    if (next === token) return;
    token = next;
    emit();
  },

  clear: (): void => {
    const wasWaiting = !answered;
    answered = true;
    if (token === null) {
      if (wasWaiting) emit();
      return;
    }
    token = null;
    emit();
  },

  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /**
   * Pick up the token the shell wrote before React mounted, and keep
   * listening for the synthetic storage events it fires on re-injection.
   * Runs once per page load no matter how many hooks call it.
   */
  bootstrap: (onToken: (next: string) => void): void => {
    if (bootstrapped || !isEmbeddedMode()) return;
    bootstrapped = true;

    const adopt = (next: string | null) => {
      if (next && next !== token) {
        onToken(next);
      }
    };

    adopt(localStorage.getItem('access_token'));

    const onStorage = (event: StorageEvent) => {
      if (event.key === 'access_token' && event.newValue) {
        devLog('[nativeSession] Token updated via storage event');
        adopt(event.newValue);
      }
    };
    window.addEventListener('storage', onStorage);

    // Bridge v2 hands the token over as a message too: after every load, on
    // returning to the foreground and after a refresh. In a browser or an
    // older wrapper nothing is ever sent.
    const stopTokens = onNative('token', ({ accessToken }) => {
      devLog('[nativeSession] Token updated via the native bridge');
      adopt(accessToken);
    });

    // No session in the wrapper: after a load with nobody signed in, or once
    // it has signed out. The stored token goes too, or the polling below
    // would adopt it again.
    const stopSignedOut = onNative('signedOut', () => {
      devLog('[nativeSession] The wrapper has no session');
      localStorage.removeItem('access_token');
      nativeSession.clear();
    });
    const stopWaiting = window.setTimeout(() => {
      if (answered || token !== null) return;
      answered = true;
      emit();
    }, SESSION_ANSWER_TIMEOUT_MS);

    // The shell may inject a beat after first paint, and the storage event
    // does not always fire. Poll for the first 5 seconds, then stop.
    const interval = window.setInterval(() => {
      adopt(localStorage.getItem('access_token'));
    }, 500);
    const stopPolling = window.setTimeout(
      () => window.clearInterval(interval),
      5000
    );

    teardown = () => {
      window.removeEventListener('storage', onStorage);
      stopTokens();
      stopSignedOut();
      window.clearTimeout(stopWaiting);
      window.clearInterval(interval);
      window.clearTimeout(stopPolling);
    };
  },

  /** Test only. */
  _reset: (): void => {
    teardown?.();
    teardown = null;
    token = null;
    bootstrapped = false;
    answered = false;
    listeners.clear();
  },
};
